import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, DatabaseBackup, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

/** A backup is only worth anything if it can be restored: take one, then test it. The test restores into a scratch database, never over the live one. */
function Backups() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => { try { setData((await api.get("/admin/backups")).data); } catch (err) { toast.error(getErrorMessage(err)); } }, []);
  useEffect(() => { load(); }, [load]);
  const run = async (key, call, done) => {
    setBusy(key);
    try { const { data: r } = await call(); (r.status === "OK" ? toast.success : toast.error)(done(r)); await load(); }
    catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(null); }
  };
  if (!data) return null;
  const { summary, runs } = data;
  const lastGood = runs.find((r) => r.kind === "BACKUP" && r.status === "OK");
  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-5" data-testid="backups-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {summary.healthy ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-amber-500" />}
          <p className="text-sm font-semibold text-foreground" data-testid="backup-health">{summary.healthy ? "Backed up, and the backup is proved to restore" : "Backups need attention"}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("backup", () => api.post("/admin/backups"), (r) => `Backup ${r.status === "OK" ? `made (${size(r.size_bytes)})` : `failed: ${r.error}`}`)} data-testid="backup-now">
            {busy === "backup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} Back up now</Button>
          <Button size="sm" disabled={!!busy || !lastGood} onClick={() => run("test", () => api.post(`/admin/backups/${lastGood.id}/test-restore`), (r) => (r.status === "OK" ? "The backup restores correctly" : `The restore test failed: ${r.error}`))} data-testid="backup-test">
            {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Test restore</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="backup-summary">
        {summary.last_backup_at ? `Last backup ${summary.last_backup_hours_ago} hours ago. ` : "No backup has been taken yet. "}
        {summary.last_passed_test_days_ago != null ? `Last passed restore test ${summary.last_passed_test_days_ago} days ago.` : "No restore test has passed yet, so these backups are not yet proved."}
      </p>
      {runs.length > 0 && (
        <ul className="divide-y divide-border text-sm" data-testid="backup-runs">
          {runs.slice(0, 8).map((r) => (
            <li key={r.id} className="py-2" data-testid={`backup-run-${r.kind}-${r.status}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>{r.kind === "BACKUP" ? "Backup" : "Restore test"} · {r.started_at ? fmtDateTime(r.started_at) : ""}{r.size_bytes ? ` · ${size(r.size_bytes)}` : ""}</span>
                <Badge variant={r.status === "OK" ? "default" : "destructive"}>{r.status === "OK" ? "OK" : r.status === "RUNNING" ? "Running" : "Failed"}</Badge>
              </div>
              {r.checks && <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">{r.checks.map((c) => <li key={c.name} className={c.ok ? "" : "text-rose-600"}>{c.ok ? "✓" : "✗"} {c.name}{c.ok ? "" : `: ${c.detail}`}</li>)}</ul>}
              {!r.checks && r.error && <p className="mt-1 text-xs text-rose-600">{r.error}</p>}
            </li>))}
        </ul>)}
    </div>
  );
}

export default function SystemHealth() {
  const [health, setHealth] = useState(null);

  useEffect(() => { api.get("/admin/health").then(({ data }) => setHealth(data)).catch((err) => toast.error(getErrorMessage(err))); }, []);

  if (!health) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="system-health-page">
      <PageHeader title="Backup & Health" description="Database connectivity, latest migration and background scheduler status." />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="health-db-card">
          <div className="flex items-center gap-2">
            {health.db_connection_ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-rose-500" />}
            <p className="text-sm font-semibold text-foreground">Database Connection</p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{health.db_connection_ok ? "Connected" : "Not connected"}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5" data-testid="health-migration-card">
          <p className="text-sm font-semibold text-foreground">Last Migration</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{health.last_migration || "—"}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5" data-testid="health-scheduler-card">
          <p className="text-sm font-semibold text-foreground">Background Scheduler</p>
          <Badge className="mt-2" variant={health.scheduler_enabled ? "default" : "secondary"}>{health.scheduler_enabled ? "Enabled" : "Disabled"}</Badge>
        </div>
        <div className="rounded-xl border border-border bg-card p-5" data-testid="health-snapshot-card">
          <p className="text-sm font-semibold text-foreground">Last Snapshot</p>
          <p className="mt-1 text-xs text-muted-foreground">{health.last_snapshot_at || "No snapshots recorded"}</p>
        </div>
      </div>
      <Backups />
    </div>
  );
}
